/**
 * placement_water.ts — the standing water the SERVER refuses a building in, judged as the server judges it
 * (2026-10-01, the owner: "make it so ponds are included, unless it breaks another feature").
 *
 * `placementProblem` (server/src/lib/structures.ts) now asks THE WATER RULE at the footprint's centre, corners and door
 * approach: a river's channel, a baked lake, a pond (flag `placement_water_v2`). The placement map must never show
 * GREEN where that refuses, so it asks the SAME rule on the SAME inputs — fishing_water.ts's law, extended to rivers:
 *   the rule     shared/water_rules.ts standingWaterKind (byte-identical to the server's);
 *   the ground   terrainHeightNoRivers — the server registers no river network, so that IS its ground;
 *   the lakes    lakes.ts bakedLakeRows — the hydrology file's lakes in the server table's rounding;
 *   the plateau  plateauLevelAt (a settlement's disc is never water), as the server passes it;
 *   the rivers   THE SERVER'S TABLE, REBUILT: shared/river_index.ts rounds the rivers this client already decoded
 *                (hydrology_load.loadedRivers) exactly as `bake:water-index` rounded them into the server's generated
 *                table, and builds and asks the same index. Not the rendered ribbon, not a tolerance — the same numbers.
 *   the pose     the f32 x / z / yaw the reducer receives (the caller rounds: placement_rules.ts).
 * `verify:placement-water` pins every one of those (byte mirrors, 0 differences over sampled shore points).
 *
 * THE POND IS THE DRAWN POND (2026-10-01 evening, "fix the 7% pond points that show no rendered water"): the judge's
 * pond floods the ground the client DRAWS (water_rules.ts pondStands), so its two further inputs are
 *   the bed      `terrainHeight` — the registered river network carved in, which is what this client draws — with a
 *                flowing channel at its water (`pondBedLevel` over `riverWaterAt` / `riverStillAt`). The server builds
 *                the same bed from THE CARVE TABLE, the same network bit for bit (server water_index.ts drawnBed;
 *                `verify:pond-parity` compares the two beds and the two answers);
 *   the sea      `commonsSeaLevel` — the rule's sea guard, the judge's own sea.
 * Before the hydrology file has loaded there is no network: the bed is the bare ground under its own cache tag, and
 * the map's rendered-water test still stands behind it.
 *
 * The client's own rendered-water test (placement_rules.ts `waterAt`) stays, after this one: the server's pond is the
 * conservative side of every client's lake bake, so the map still refuses rendered water the server would accept —
 * stricter, never greener.
 */
import { standingWaterKind, pondBedLevel, RIVER_CAST_SLACK_M, RIVER_STAND_SLACK_M, type StandingKind } from '../../../shared/water_rules.js';
import { riverTablePoints, riverTableArrays, buildRiverIndex, riverIndexAt, type RiverIndex, type RiverTablePoint } from '../../../shared/river_index.js';
import { terrainHeight, terrainHeightNoRivers, riverWaterAt, riverStillAt, plateauLevelAt, RIVER_STRIDE } from '../../../shared/terrain.js';
import { bakedLakeRows } from './lakes.js';
import { commonsSeaLevel } from './sea_level.js';

export type { StandingKind } from '../../../shared/water_rules.js';
export type StandingAt = (x: number, z: number) => StandingKind | null;

/** One index per decoded river list (hydrology_load keeps one list per seed for the session). */
const indexes = new WeakMap<object, RiverIndex>();

/** The server's river table and index, rebuilt from the decoded rivers (flat RIVER_STRIDE rows per river, in the
 *  hydrology file's own order — the bake's order). Built once per list (~0.2 s for the Commons' 103k points). */
export function serverRiverIndex(rivers: ReadonlyArray<ArrayLike<number>>): RiverIndex {
  let ix = indexes.get(rivers);
  if (!ix) {
    const table: RiverTablePoint[][] = [];
    for (const r of rivers) { const pts = riverTablePoints(r, RIVER_STRIDE); if (pts.length >= 2) table.push(pts); }
    const a = riverTableArrays(table);
    ix = buildRiverIndex(a.x, a.z, a.hw, a.seg, RIVER_CAST_SLACK_M); // the server's reach (water_index.ts buildIndex)
    indexes.set(rivers, ix);
  }
  return ix;
}

/** Is (x, z) in a river's own channel, as the server's `riverAt(seed, x, z, RIVER_STAND_SLACK_M)` answers? */
export function serverRiverStand(rivers: ReadonlyArray<ArrayLike<number>>, x: number, z: number): boolean {
  return riverIndexAt(serverRiverIndex(rivers), x, z, RIVER_STAND_SLACK_M) !== null;
}

/**
 * The server's standing-water test for the Commons seed: (x, z) → 'river' | 'lake' | 'pond' | null. `rivers` = the
 * decoded hydrology rivers (null until the file has loaded: the river test then answers nothing — the map's own
 * rendered-water test still stands behind it). Hand it the f32 points the server will see.
 */
export function serverStandingWater(seed: string, rivers: ReadonlyArray<ArrayLike<number>> | null): StandingAt {
  const river = rivers
    ? (x: number, z: number, slack: number): boolean => riverIndexAt(serverRiverIndex(rivers), x, z, slack) !== null
    : (): boolean => false;
  const height = (a: number, b: number): number => terrainHeightNoRivers(seed, a, b);
  const dry = (a: number, b: number): boolean => plateauLevelAt(seed, a, b) !== null;
  const bed = (a: number, b: number): number => drawnBed(seed, a, b);
  const sea = commonsSeaLevel(seed);
  // One bed per cache tag (water_rules.ts): the bare ground before the network arrives must not answer for the carved.
  const tag = rivers ? seed : `${seed}|bare`;
  return (x, z) => standingWaterKind(river, bakedLakeRows(seed), tag, height, x, z, dry, bed, sea);
}

/** The lake bake's bed as this client draws it: the terrain with the registered river network carved in, a flowing
 *  channel at its water (lakes.ts fillRows' own cap, over the analytic ground). The server's `drawnBed`. */
export function drawnBed(seed: string, x: number, z: number): number {
  return pondBedLevel(terrainHeight(seed, x, z), riverWaterAt(seed, x, z), riverStillAt(seed, x, z));
}
